import { supabase } from '../lib/supabase';

type ModerationStatus = 'pending' | 'approved' | 'rejected';
type ReportStatus = 'pending' | 'resolved' | 'rejected';
type ReportedItemType = 'post' | 'profile' | 'comment';

const MODERATOR_ROLES = ['moderator', 'admin'];

const ensureModerator = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('No autenticado');
  }

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('user_type')
    .eq('id', user.id)
    .maybeSingle();

  if (error) throw error;

  if (!profile || !MODERATOR_ROLES.includes(profile.user_type)) {
    throw new Error('No tienes permisos de moderación.');
  }
};

export const moderationService = {
  async getComments(options?: {
    status?: ModerationStatus | 'hidden' | 'all';
    searchQuery?: string;
    limit?: number;
  }) {
    await ensureModerator();

    let query = supabase
      .from('comments')
      .select(`
        *,
        profiles:profiles!comments_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        posts:posts!comments_post_id_fkey (
          id,
          title
        )
      `)
      .order('created_at', { ascending: false });

    if (options?.status && options.status !== 'all') {
      query = query.eq('moderation_status', options.status);
    }

    if (options?.searchQuery) {
      const term = options.searchQuery.trim();
      if (term) {
        query = query.or(
          `content.ilike.%${term}%,profiles.username.ilike.%${term}%,posts.title.ilike.%${term}%`
        );
      }
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },
  async getPosts(options?: {
    status?: ModerationStatus | 'all';
    searchQuery?: string;
    limit?: number;
  }) {
    await ensureModerator();

    let query = supabase
      .from('posts')
      .select(`
        *,
        profiles!posts_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          user_type
        ),
        media_files (
          id,
          type,
          url,
          file_name
        )
      `)
      .order('created_at', { ascending: false });

    if (options?.status && options.status !== 'all') {
      query = query.eq('moderation_status', options.status);
    }

    if (options?.searchQuery) {
      const term = options.searchQuery.trim();
      if (term) {
        query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%,profiles.username.ilike.%${term}%`);
      }
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getProfiles(options?: {
    status?: 'active' | 'warned' | 'suspended' | 'banned' | 'all';
    searchQuery?: string;
    limit?: number;
  }) {
    await ensureModerator();

    let query = supabase
      .from('profiles')
      .select('*')
      .neq('user_type', 'admin')
      .order('created_at', { ascending: false });

    if (options?.status && options.status !== 'all') {
      query = query.eq('account_status', options.status);
    }

    if (options?.searchQuery) {
      const term = options.searchQuery.trim();
      if (term) {
        query = query.or(`username.ilike.%${term}%,full_name.ilike.%${term}%`);
      }
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getReports(options?: {
    status?: ReportStatus | 'all';
    type?: ReportedItemType | 'all';
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) {
    await ensureModerator();

    let query = supabase
      .from('reports')
      .select(`
        *,
        reporter:profiles!reports_reporter_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        ),
        moderator:profiles!reports_moderator_id_fkey (
          id,
          username,
          full_name
        )
      `)
      .order('created_at', { ascending: false });

    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    if (options?.type && options.type !== 'all') {
      query = query.eq('reported_item_type', options.type);
    }

    if (options?.startDate) {
      query = query.gte('created_at', options.startDate);
    }

    if (options?.endDate) {
      query = query.lte('created_at', options.endDate);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;
    if (error) throw error;

    const reports = data || [];

    const profileIds = Array.from(new Set(
      reports
        .filter(report => report.reported_item_type === 'profile')
        .map(report => report.reported_item_id)
    ));

    const postIds = Array.from(new Set(
      reports
        .filter(report => report.reported_item_type === 'post')
        .map(report => report.reported_item_id)
    ));

    const commentIds = Array.from(new Set(
      reports
        .filter(report => report.reported_item_type === 'comment')
        .map(report => report.reported_item_id)
    ));

    const [reportedProfiles, reportedPosts, reportedComments] = await Promise.all([
      profileIds.length > 0
        ? supabase
          .from('profiles')
          .select('id, username, full_name, avatar_url')
          .in('id', profileIds)
        : Promise.resolve({ data: [], error: null }),
      postIds.length > 0
        ? supabase
          .from('posts')
          .select('id, title')
          .in('id', postIds)
        : Promise.resolve({ data: [], error: null }),
      commentIds.length > 0
        ? supabase
          .from('comments')
          .select('id, content')
          .in('id', commentIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (reportedProfiles.error) throw reportedProfiles.error;
    if (reportedPosts.error) throw reportedPosts.error;
    if (reportedComments.error) throw reportedComments.error;

    const profilesMap = new Map(
      (reportedProfiles.data || []).map((profile) => [profile.id, profile])
    );
    const postsMap = new Map(
      (reportedPosts.data || []).map((post) => [post.id, post])
    );
    const commentsMap = new Map(
      (reportedComments.data || []).map((comment) => [comment.id, comment])
    );

    return reports.map((report) => ({
      ...report,
      reported_profile: profilesMap.get(report.reported_item_id) || null,
      reported_post: postsMap.get(report.reported_item_id) || null,
      reported_comment: commentsMap.get(report.reported_item_id) || null,
    }));
  },

  async moderatePost(params: {
    postId: string;
    action: ModerationStatus | 'pending';
    reason?: string;
    reportId?: string;
  }) {
    await ensureModerator();

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    try {
      const { data, error } = await supabase.rpc('perform_post_moderation', {
        p_action: params.action,
        p_post_id: params.postId,
        p_reason: params.reason ?? null,
        p_report_id: params.reportId ?? null,
      });

      if (error) throw error;
      return data;
    } catch (rpcError) {
      console.warn('RPC perform_post_moderation failed, using direct update:', rpcError);

      const status = params.action === 'pending' ? 'pending' : params.action;

      const { data: updatedPost, error: updateError } = await supabase
        .from('posts')
        .update({
          moderation_status: status,
          moderation_reason: params.reason || null,
          moderated_by: user.id,
          moderated_at: new Date().toISOString(),
        })
        .eq('id', params.postId)
        .select('*')
        .single();

      if (updateError) throw updateError;
      return updatedPost;
    }
  },

  async moderateComment(params: {
    commentId: string;
    action: ModerationStatus | 'pending';
    reason?: string;
    reportId?: string;
  }) {
    await ensureModerator();

    // Check auth for fallback usage
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    try {
      const { data, error } = await supabase.rpc('perform_comment_moderation', {
        p_action: params.action,
        p_comment_id: params.commentId,
        p_reason: params.reason ?? null,
        p_report_id: params.reportId ?? null,
      });

      if (error) throw error;
      return data;
    } catch (rpcError) {
      console.warn('RPC perform_comment_moderation failed, using direct update:', rpcError);

      const { data, error } = await supabase
        .from('comments')
        .update({
          moderation_status: params.action,
          moderation_reason: params.reason || null,
          moderated_by: user.id,
          moderated_at: new Date().toISOString(),
        })
        .eq('id', params.commentId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    }
  },

  async moderateProfile(params: {
    profileId: string;
    status: 'active' | 'warned' | 'suspended' | 'banned';
    reason?: string;
    suspendedUntil?: string | null;
    reportId?: string;
  }) {
    await ensureModerator();

    const { data, error } = await supabase.rpc('perform_profile_moderation', {
      p_profile_id: params.profileId,
      p_reason: params.reason ?? null,
      p_report_id: params.reportId ?? null,
      p_status: params.status,
      p_suspended_until: params.suspendedUntil ?? null,
    });

    if (error) throw error;
    return data;
  },

  async updateReportStatus(reportId: string, status: ReportStatus, notes?: string) {
    await ensureModerator();

    const { data, error } = await supabase.rpc('update_report_status', {
      p_report_id: reportId,
      p_status: status,
      p_notes: notes || null,
    });

    if (error) throw error;
    return data;
  },

  async getModerationActions(params: {
    itemType?: ReportedItemType;
    itemId?: string;
    limit?: number;
  } = {}) {
    await ensureModerator();

    let query = supabase
      .from('moderation_actions')
      .select('*')
      .order('created_at', { ascending: false });

    if (params.itemType) {
      query = query.eq('item_type', params.itemType);
    }

    if (params.itemId) {
      query = query.eq('item_id', params.itemId);
    }

    if (params.limit) {
      query = query.limit(params.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getTransparencyReportData(options: { startDate: string; endDate: string }) {
    await ensureModerator();

    const { startDate, endDate } = options;

    // 1. Get total number of reports in the period
    const { count: totalReports, error: totalError } = await supabase
      .from('reports')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (totalError) throw totalError;

    // 2. Get resolved/rejected reports to calculate response time
    const { data: resolvedReports, error: resolvedError } = await supabase
      .from('reports')
      .select('created_at, updated_at')
      .in('status', ['resolved', 'rejected'])
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (resolvedError) throw resolvedError;

    let totalResponseTime = 0;
    resolvedReports.forEach(report => {
      const created = new Date(report.created_at).getTime();
      const updated = new Date(report.updated_at).getTime();
      totalResponseTime += (updated - created);
    });

    const averageResponseTimeMs = resolvedReports.length > 0 ? totalResponseTime / resolvedReports.length : 0;
    const averageResponseTimeHours = averageResponseTimeMs / (1000 * 60 * 60);

    // 3. Get a breakdown of moderation actions taken
    const { data: actions, error: actionsError } = await supabase
      .from('moderation_actions')
      .select('action')
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (actionsError) throw actionsError;

    const actionsSummary = actions.reduce((acc, { action }) => {
      acc[action] = (acc[action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalReports: totalReports || 0,
      resolvedReportsCount: resolvedReports.length,
      averageResponseTimeHours: parseFloat(averageResponseTimeHours.toFixed(2)),
      actionsSummary,
      startDate,
      endDate,
    };
  },

  async getModerationActionsByReportIds(reportIds: string[]) {
    await ensureModerator();

    if (!reportIds || reportIds.length === 0) return [] as Array<Record<string, unknown>>;

    const { data, error } = await supabase
      .from('moderation_actions')
      .select(`
        id,
        report_id,
        item_type,
        item_id,
        action,
        reason,
        moderator_id,
        created_at
      `)
      .in('report_id', reportIds)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },
  async getReportById(reportId: string) {
    await ensureModerator();

    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        reporter:profiles!reports_reporter_id_fkey (id, username, full_name, avatar_url)
      `)
      .eq('id', reportId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Report not found');

    const rep = data as Record<string, unknown>;
    if (rep.reported_item_type === 'profile') {
      const { data: prof } = await supabase.from('profiles').select('id, username, full_name, avatar_url').eq('id', rep.reported_item_id as string).maybeSingle();
      rep.reported_profile = prof || null;
    } else if (rep.reported_item_type === 'post') {
      const { data: post } = await supabase.from('posts').select('id, title').eq('id', rep.reported_item_id as string).maybeSingle();
      rep.reported_post = post || null;
    } else if (rep.reported_item_type === 'comment') {
      const { data: comment } = await supabase.from('comments').select('id, content, post_id').eq('id', rep.reported_item_id as string).maybeSingle();
      rep.reported_comment = comment || null;
    }
    return rep;
  },

  async getActionsForReport(reportId: string) {
    await ensureModerator();
    const { data, error } = await supabase
      .from('moderation_actions')
      .select('id, action, reason, item_type, item_id, moderator_id, created_at')
      .eq('report_id', reportId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    const actions = (data || []) as Array<Record<string, unknown>>;
    const moderatorIds = Array.from(
      new Set(actions.map((a) => a.moderator_id).filter((id) => typeof id === 'string') as string[])
    );
    let moderatorsMap = new Map<string, Record<string, unknown>>();
    if (moderatorIds.length) {
      const { data: moderators } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url')
        .in('id', moderatorIds);
      moderatorsMap = new Map(
        (moderators || [] as Array<Record<string, unknown>>).map((m) => [m.id as string, m])
      );
    }
    return actions.map((a) => ({ ...a, moderator: moderatorsMap.get(a.moderator_id as string) || null }));
  },
};
